import { AxiosInstance } from 'axios';
import { STATS_TRANSLATION } from './constants';

interface IStatsMonth {
  profitability: number;
  volatility: number;
  sharpe_ratio: number;
}

export interface IStatsYear {
  '1'?: number;
  '2'?: number;
  '3'?: number;
  '4'?: number;
  '5'?: number;
  '6'?: number;
  '7'?: number;
  '8'?: number;
  '9'?: number;
  '10'?: number;
  '11'?: number;
  '12'?: number;
  year: number;
  accrued: number;
}
type IStatsYearType = Record<string, IStatsYear>;

export interface ITimeframeStats {
  last_3_months: IStatsMonth;
  last_6_months: IStatsMonth;
  last_12_months: IStatsMonth;
  last_24_months: IStatsMonth;
  last_36_months: IStatsMonth;
  last_48_months: IStatsMonth;
  last_60_months: IStatsMonth;
  ytd: IStatsMonth;
  mtd: IStatsMonth;
  begin: IStatsMonth;
}

export interface IStockStatsHttpResponse {
  stats: {
    best_monthly_return: number;
    worst_monthly_return: number;
    positive_months: number;
    negative_months: number;
    timeframe: ITimeframeStats;
    first_quote_date: number;
    last_quote_date: number;
  };
  nicename: string;
  years?: IStatsYearType;
}

export interface IIndexHeadersHttpResponse {
  index_ticker: string;
  asset_description: string;
  nicename: string;
  unique_slug: string;
  trading_currency: 'BRL';
  start_date: number;
  last_date: number;
  last_update: number;
  has_quotes: boolean;
  situation: string;
  canonical_url: string;
}
export interface IHeadersFormatted {
  slug: string;
  displayName: string;
  value: string | number;
}

export interface IStatsFormatted {
  type: 'percent' | 'money' | 'number';
  slug: string;
  displayName: string;
  value: number;
}

export interface IResponseDTO {
  slug: string;
  nicename: string;
  pageTitle: string;
  headers: IIndexHeadersHttpResponse;
  stats: IStockStatsHttpResponse;
  formattedHeaders: IHeadersFormatted[];
  formattedStats: IStatsFormatted[];
  first_quote_date: number;
  last_quote_date: number;
  canonical_url: string;
}

export class SheetIndexUseCase {
  constructor(private readonly http: AxiosInstance) {}

  async execute(slug: string): Promise<IResponseDTO> {
    const [headers, stats] = await Promise.all([
      this.fetchHeaders(slug),
      this.fetchStats(slug),
    ]);

    return {
      slug: slug,
      nicename: headers.nicename,
      pageTitle: headers.nicename,
      stats: stats,
      headers: headers,
      first_quote_date: stats?.stats?.first_quote_date || null,
      last_quote_date: stats?.stats?.last_quote_date || null,
      formattedHeaders: this.headersFormatter(headers),
      formattedStats: this.statsFormatter(stats),
      canonical_url: headers.canonical_url,
    };
  }

  private async fetchHeaders(slug: string) {
    try {
      const { data } = await this.http.get<IIndexHeadersHttpResponse>(
        `indexes/header/${slug}/full`
      );

      return data;
    } catch (error) {
      throw error;
    }
  }

  private async fetchStats(slug: string) {
    try {
      const { data } = await this.http.get<IStockStatsHttpResponse>(
        `indexes/stats/${slug}/details`
      );

      return data;
    } catch (error) {
      return null;
    }
  }

  private headersFormatter(
    headers: IIndexHeadersHttpResponse
  ): IHeadersFormatted[] {
    const structuredHeaders: IHeadersFormatted[] = [
      {
        slug: 'description',
        displayName: 'Descrição',
        value: headers.asset_description || '-',
      },
    ];

    return structuredHeaders;
  }

  private statsFormatter(stats: IStockStatsHttpResponse): IStatsFormatted[] {
    const formattedStats: IStatsFormatted[] = [
      {
        displayName: STATS_TRANSLATION['timeframe']['begin']['profitability'],
        slug: 'profitability_begin',
        value: stats?.stats?.timeframe?.begin?.profitability || null,
        type: 'percent',
      },
      {
        displayName: STATS_TRANSLATION['timeframe']['begin']['volatility'],
        slug: 'volatility_begin',
        value: stats?.stats?.timeframe?.begin?.volatility || null,
        type: 'percent',
      },
      {
        displayName: STATS_TRANSLATION['timeframe']['begin']['sharpe_ratio'],
        slug: 'sharpe_ratio_begin',
        value: stats?.stats?.timeframe?.begin?.sharpe_ratio || null,
        type: 'number',
      },
      {
        displayName:
          STATS_TRANSLATION['timeframe']['last_12_months']['profitability'],
        slug: 'profitability12M',
        value: stats?.stats?.timeframe?.last_12_months?.profitability || null,
        type: 'percent',
      },
      {
        displayName:
          STATS_TRANSLATION['timeframe']['last_12_months']['volatility'],
        slug: 'volatility12M',
        value: stats?.stats?.timeframe?.last_12_months?.volatility || null,
        type: 'percent',
      },
      {
        displayName:
          STATS_TRANSLATION['timeframe']['last_12_months']['sharpe_ratio'],
        slug: 'sharpe_ratio12M',
        value: stats?.stats?.timeframe?.last_12_months?.sharpe_ratio || null,
        type: 'number',
      },
    ];

    return formattedStats;
  }
}
